// @flow
/**
 * Initial state value, reducers (we don't use them much) and store selectors.
 */

import { Howl } from "howler";
import { applyMiddleware, createStore } from "redux";
import thunkMiddleware from "redux-thunk";
import { composeWithDevTools } from "redux-devtools-extension";
import * as Sentry from "@sentry/react";
// https://ramdajs.com/docs/
import { find, findIndex, lensPath, propEq, set, view } from "ramda";

import {
  converstationHistoryKey,
  financialChatActivated,
  isNarrowScreen,
  leadIDKey,
  nextPageUrlKey,
  teaserClosedKey,
  teaserShownTimesKey,
  UUIDKey,
} from "./lib/constants";
import type { GlobalState, Module, RichModule } from "./lib/types";
import { ChatModes, SlackAnimationStatus } from "./lib/types";
import { ModuleExtensionMixin } from "./actions";
import { urlConfig } from "./lib/utils";

/**
 * When user goes for `link` module and gets redirected to another page -
 * we store this (next) page url in localStorage.
 * And open the chat with the last state next time the chat gets loaded for user
 * if his new page matches the stored one.
 * @returns {boolean}
 */
export const getShouldRestoreSession = () => {
  const savedUrl = localStorage.getItem(nextPageUrlKey);
  if (savedUrl) {
    localStorage.removeItem(nextPageUrlKey);
  }
  return savedUrl === window.location.href;
};

const getInitialState: GlobalState = () => {
  return {
    botMessageSound: new Howl({
      // $FlowFixMe
      src: [require("./static/pop.mp3")],
      volume: 0.5,
    }),

    isTeaserVisible: false,
    slackAnimation: {
      typingAnimation: SlackAnimationStatus.hide, // show|hide|off
      actionTimeout: null,
      animationTimeout: null,
    },
    activeModule: null,
    config: null, // Should be empty here because of the way `isConfigLoaded` written.
    modules: {}, // Modules received from the server.
    messagesHistory: [], // Used in rendering.
    localHistoryLoaded: false,
    localHistoryExists: JSON.parse(localStorage.getItem(converstationHistoryKey) || "[]").length > 2,
    leadID: localStorage.getItem(leadIDKey) || null,
    uuid: localStorage.getItem(UUIDKey) || null,
    visitorUUID: null, // Never stored in localStorage since the session iframe will be opened every time anyway.
    teaserClosed: localStorage.getItem(teaserClosedKey),
    shouldRestoreSession: getShouldRestoreSession(),
    userData: {
      date: null,
      tour_time: null,
      email: "",
      first_name: "",
      phone: "",
    },
    originalPageTitle: window.document.title,
    titleFlashingInterval: null,
    conversation: [], // History of conversation to get fully sent to server upon lead generation.
    textInputValue: "",
    inputErrors: {}, // User input validation errors per message.
    editingMessageNum: null, // Number of the message which user is editing.
    workWithHistory: false, // Is user answering the last message or editing an older message.
    showTextInput: false,
    showDatePicker: false,
    showConfirmSelectionButton: false,
    familyAssetsAdmin: false,
    // If true - managers wont reply to leads during weekends, but the chat will show a notice about this.
    weekendInactive: false,
    isWeekend: [6, 0].includes(new Date().getDay()),
    maxAnswersToInitiallyShow: 3, // How many answers at max show in the first module.
    showAllAnswerOptions: false, // Are all answer options expanded in the first module.
    chatMode: urlConfig.forceOpen ? ChatModes.open : ChatModes.circle, // Current "mode" of the chat.
    timesAvailable: {}, // map {int weekDay: [[int timeStartHour, int timeEndHour]]} of time slots available for tour booking.
    timezoneName: "", // Facility timezone name.
    timezoneOffset: "", // Facility timezone offset from UTC, e.g. "-7:00".
    slackController: null,
    jobApplicantionSubmitted: false,
    ia: {
      suggestions: [],
      wrongAnswersInRow: 0,
      hasGreeted: false,
      lastQuestion: "",
      queue: [],
      showQuickActions: false,
    },
    guide: {
      selectedCategory: "Childcare",
    },
  };
};

export const actionTypes = {
  SET_CONFIG: "SET_CONFIG",
  STORE_MESSAGE: "STORE_MESSAGE",
  SET_MESSAGE_ANSWER: "SET_MESSAGE_ANSWER",
  ADD_TO_ARRAY: "ADD_TO_ARRAY",
  DEEP_CHANGE: "DEEP_CHANGE", // Universal action when I'm lazy to write a ton of small ones.
  CLEAR_TEXT_INPUT: "CLEAR_TEXT_INPUT",
};

export const reducer = (state: GlobalState = getInitialState(), action: any) => {
  let path;
  let value;
  let messageNum;
  let answer;

  switch (action.type) {
    case actionTypes.SET_CONFIG:
      state = { ...state, ...action.payload };
      break;
    case actionTypes.STORE_MESSAGE:
      state = {
        ...state,
        messagesHistory: [...state.messagesHistory, action.payload],
      };
      break;
    case actionTypes.SET_MESSAGE_ANSWER:
      ({ messageNum, answer } = action.payload);
      path = lensPath(["messagesHistory", messageNum, "answer"]);
      state = set(path, answer, state);
      break;
    case actionTypes.ADD_TO_ARRAY:
      ({ path, value } = action.payload);
      if (typeof path === "string") {
        path = path.split(".");
        path = path.map((p) => (Number.isInteger(parseInt(p)) ? parseInt(p) : p));
      }
      path = lensPath(path);
      state = set(path, [...(view(path, state) || []), value], state);
      break;
    default:
      break;
  }
  if (action.type.slice(0, 11) === "DEEP_CHANGE") {
    ({ path, value } = action.payload);
    if (typeof path === "string") {
      path = path.split(".");
    }
    path = path.map((p) => (Number.isInteger(parseInt(p)) ? parseInt(p) : p));
    path = lensPath(path);
    state = set(path, value, state);
  }
  return state;
};

const sentryReduxEnhancer = Sentry.createReduxEnhancer();

/**
 * Initialize the mighty Store!
 * Called below right away.
 * But still exported for usage in tests.
 */
export const initializeStore = (initialState: GlobalState = getInitialState()) => {
  const composeEnhancers = composeWithDevTools({ trace: true });
  return createStore(reducer, initialState, composeEnhancers(applyMiddleware(thunkMiddleware), sentryReduxEnhancer));
};

/**
 * All of a sudden I need a way to dispatch actions from outside of components.
 */
export const store = initializeStore();
export const dispatch = store.dispatch;

// SELECTORS

export const isConfigLoaded = (state: GlobalState) => {
  return state.config && Object.keys(state.config).length > 0;
};

export const getAdvisorName = (state: GlobalState) => {
  return (state.config && state.config.advisorName) || "Community Assistant";
};

export const getWidgetBackground = (state: GlobalState) => {
  return (state.config && state.config.backgroundColor) || "#fff";
};

export const getChatButtonBackground = (state: GlobalState) => {
  return (state.config && state.config.buttonColor) || "#fff";
};

export const getTextColor = (state: GlobalState) => {
  return (state.config && state.config.color) || "#222";
};

export const getTeaserPause = (state: GlobalState) => {
  return (state.config ? state.config.pause : 0) * 1000 || 0;
};

export const getTeaserScrollPause = (state: GlobalState) => {
  return (state.config && state.config.teaserScrollDelay) || "0px";
};

export const getTeaserText = (state: GlobalState) => {
  return state.config && state.config.teaser;
};

export const getBrandColor = (state: GlobalState) => {
  return (state.config && state.config.brandColor) || "#387BFF";
};

export const getFontFamily = (state: GlobalState) => {
  return (state.config && state.config.fontFamily) || "arial";
};

export const getFontSize = (state: GlobalState) => {
  return (state.config && state.config.fontSize) || "";
};

export const getIconLogo = (state: GlobalState) => {
  const defaultIcon = "https://s3.amazonaws.com/stage-knock-doorway-builds/bubble.png";
  return (state.config && state.config.iconLogo) || defaultIcon;
};

/**
 * Get intro text.
 * @param state
 * @returns {*|string|string}
 */
export const getIntroText = (state: GlobalState) => {
  return (state.config && state.config.introText) || "Hello!";
};

/**
 * Define the chat position on the page - on the left or on the right.
 * @param state
 * @returns {*|string}
 */
export const getChatPosition = (state: GlobalState) => {
  return (state.config && state.config.position) || "right";
};

/**
 * How many time to show the teaser to a single user at max.
 * @param state
 * @returns {*|number}
 */
export const getShowTeaserMaxTimes = (state: GlobalState) => {
  return (state.config && state.config.showMaxTimes) || 5;
};

/**
 * Is the tour at the same day is allowed.
 * @param state
 * @returns {*|boolean}
 */
export const getSameDayTour = (state: GlobalState) => {
  return (state.config && state.config.sameDayTour) || false;
};

export const getShowTitleFlash = (state: GlobalState) => {
  return (state.config && state.config.showTitleFlash) || false;
};

export const getDisableSmallerDevice = (state: GlobalState) => {
  return (state.config && state.config.disableSmallerDevices) || false;
};

export const getDisableMobile = (state: GlobalState) => {
  return (state.config && state.config.disableMobile) || urlConfig.disableMobile || false;
};

export const getMessagesHistory = (state: GlobalState) => {
  return state.messagesHistory;
};

export const getModules = (state: GlobalState) => {
  return state.modules;
};

export const getFirstModule = (state: GlobalState) => {
  return getModules(state)[1];
};

export const getMessageAnswer = ({ state, messageNum }: { state: GlobalState, messageNum: number }) => {
  return state.messagesHistory[messageNum].answer;
};

export const getMessageFromHistory = ({ state, messageNum }: { state: GlobalState, messageNum: number }) => {
  return state.messagesHistory[messageNum];
};

export const getActiveModule: RichModule = (state: GlobalState) => {
  return state.activeModule;
};

export const getIsChatOpen = (state: GlobalState) => {
  return state.chatMode === ChatModes.open;
};

export const getChatMode = (state: GlobalState) => {
  return state.chatMode;
};

export const getLastMessageNumber = (state: GlobalState) => {
  return state.messagesHistory.length - 1;
};

export const getLastMessageType = (state: GlobalState) => {
  return state.messagesHistory.length && state.messagesHistory.slice(-1)[0].type;
};

export const getTeaserShownTimes = () => {
  return JSON.parse(localStorage.getItem(teaserShownTimesKey) || "0");
};

export const getFullNameRequired = (state: GlobalState) => {
  return (state.config && state.config.fullNameRequired) || false;
};

/**
 * If the teaser was ever closed by pressing the 'x' button in it -
 * we keep in localStorage and this function retrieves it.
 * @param state
 * @returns {string | boolean | *}
 */
export const wasTeaserClosed = (state: GlobalState) => {
  return state.teaserClosed;
};

export const getTeaserDotColor = (state: GlobalState) => {
  return (state.config && state.config.teaserDotColor) || "#06c506";
};

/**
 * Get community name.
 */
export const getCommunityName = (state: GlobalState) => {
  return (state.config && state.config.name) || "";
};

/**
 * Find the module by role.
 * @param state
 * @param role
 */
export const getModuleByRole = (state: GlobalState, role: string) => {
  const modules = getModules(state);
  const id = Object.keys(modules).find((k) => modules[k].role === role);
  if (isNaN(id)) {
    return null;
  }
  return { ...modules[id], id: parseInt(id) };
};

/**
 * Find the module by type.
 * @param state
 * @param type
 */
export const getModuleByType = (state: GlobalState, type: string) => {
  const modules = getModules(state);
  // if (!modules) {
  //   return null;
  // }
  const id = Object.keys(modules).find((k) => modules[k].type === type);
  if (isNaN(id)) {
    return null;
  }
  return { ...modules[id], id: parseInt(id) };
};

/**
 * Get slack animation state object.
 * @param state
 * @returns {string|string}
 */
export const getSlackAnimationStatus = (state: GlobalState) => {
  return state.slackAnimation;
};

/**
 * Get url for sending out the chat answers.
 * @param state
 * @returns {string|string}
 */
export const getChatUpdateUrl = (state: GlobalState) => {
  return state.config && state.config.apiChatUpdateURL;
};

/**
 * Get url for sending out the chat events.
 * @param state
 * @returns {string}
 */
export const getChatEventUrl = (state: GlobalState) => {
  return state.config && state.config.apiChatEventURL;
};

/**
 * Get url for performing the locations search.
 * @param state
 * @returns {string}
 */
export const getLocationsUrl = (state: GlobalState) => {
  return state.config && state.config.apiLocationsURL;
};

/**
 * Get url for updating the Visitor.
 * @param state
 * @returns {string}
 */
export const getVisitorUpdateUrl = (state: GlobalState) => {
  return state.config && state.config.visitorUpdateURL;
};

/**
 * Get url for sending out the chat events about out_of_budget user's status .
 * @param state
 * @returns {string}
 */
export const chatOutOfBudgetUrl = (state: GlobalState) => {
  return state.config && state.config.apiChatOutOfBudgetURL;
};

/**
 * Get url for sending out job applications.
 * @param state
 * @returns {string|string}
 */
export const getJobApplicantUrl = (state: GlobalState) => {
  return state.config && state.config.apiJobApplicantURL;
};

/**
 * Get value of the user input field.
 * TODO: move this from the global state to the TextInput component's state.
 * @param state
 * @returns {string}
 */
export const getTextInputValue = (state: GlobalState) => {
  return state.textInputValue;
};

/**
 * Text used to customize pricing result type. Could be Monthly Rate, Monthly Bundle, Day Rate, etc.
 * @param state
 * @returns {string}
 */
export const getFloorPlanUrl = (state: GlobalState) => {
  return (state.config && state.config.floorPlanUrl) || null;
};

/**
 * Get user ID.
 * @param state
 */
export const getUserID = (state: GlobalState) => {
  return state.uuid;
};

/**
 * Get visitor ID.
 * @param state
 */
export const getVisitorID = (state: GlobalState) => {
  return state.visitorUUID;
};

/**
 * Return time slots available for booking.
 * @param state
 * @returns {{[p: number]: Array<Array<number>>}}
 */
export const getTimesAvailable = (state: GlobalState) => {
  return state.config.timesAvailable || {};
};

/**
 * Get facility timezone name.
 * @param state
 * @returns {string}
 */
export const getTimezoneName = (state: GlobalState) => {
  return state.config.timezoneName;
};

/**
 * Get facility timezone offset from UTC. E.g. "-7:00".
 * @param state
 * @returns {string|string}
 */
export const getTimezoneOffset = (state: GlobalState) => {
  return state.config.timezoneOffset;
};

/**
 * Return `true` if the pricing module should show only the price that is related to
 * the care type that used has selected.
 * @param state
 */
export const getShouldFilterPricing = (state: GlobalState) => {
  return state.config.filterPricing || false;
};

/**
 * Get module by it's ID.
 * @param state
 * @param moduleNum
 * @returns {{}}
 */
export const getModuleById = (state: GlobalState, moduleNum: number): Module => {
  return {
    ...getModules(state)[moduleNum],
    ...ModuleExtensionMixin,
    id: parseInt(moduleNum),
  };
};

/**
 * Get provider ID.
 * @param state
 * @returns {number}
 */
export const getProviderID = (state: GlobalState) => {
  return state.config.providerID;
};
/**
 * One weird thing for the weird times ¯\_(ツ)_/¯
 * @param dispatch
 * @param getState
 * @returns {*}
 */
export const getLatestState = (dispatch: any, getState: GlobalState) => {
  return getState();
};

export const getShouldShowContactInformation = (state: GlobalState) => {
  return state.config.showContactInformationMessage || urlConfig.shouldShowContactInformation;
};

export const getShouldShowConfirmSelection = (state: GlobalState) => {
  return state.showConfirmSelectionButton;
};

export const getShouldShowTextInput = (state: GlobalState) => {
  return state.showTextInput;
};

export const getShouldShowDatePicker = (state: GlobalState) => {
  return state.showDatePicker;
};

export const getIsTeaserVisible = (state: GlobalState) => {
  return state.isTeaserVisible;
};

export const getLeadId = (state: GlobalState) => {
  return state.leadID;
};

export const getModulesByName = (state: GlobalState) => {
  return state.config.modulesByName || {};
};

export const getShowAllAnswerOptions = (state: GlobalState) => {
  return state.showAllAnswerOptions;
};

export const getMaxAnswersToInitiallyShow = (state: GlobalState) => {
  return state.maxAnswersToInitiallyShow;
};

export const getJobApplicationSubmitted = (state: GlobalState) => {
  return state.jobApplicationSubmitted;
};

export const getMaxPhoneAttempts = (state: GlobalState) => {
  return state.config && state.config.maxPhoneAttempts;
};

/**
 * Get if user wants to speak with expert based on some answer.
 */
export const getWantsToSpeakWithExpert = (state: GlobalState) => {
  return Boolean(state.userData.speak_with_expert && state.userData.speak_with_expert.includes("Yes"));
};

/**
 * Get user monthly income based on some answer.
 */
export const getUserMonthlyIncome = (state: GlobalState) => {
  let income = state.userData && state.userData.monthly_income;

  if (!income) {
    return null;
  }

  // Remove "+" from the end. "$5,000+"
  if (income.slice(-1) === "+") {
    income = income.slice(0, -1);
  }

  let incomeInt = null;

  try {
    incomeInt = parseInt(income.replace("$", "").replace(",", ""));
  } catch (e) {
    Sentry.withScope(function (scope) {
      Sentry.captureException(e);
    });
    console.log("FAM error:");
    console.log(e);
  }

  return incomeInt;
};

export const getFacilityCoordinates = (state: GlobalState) => {
  if (state.config.coordinates) {
    return state.config.coordinates;
  }
  return [32.81073760880601, -79.94498956822284];
};

export const getChatPk = (state: GlobalState) => {
  return state.config.pk;
};
